import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Http } from '../utilities/legacy-http';
import * as Data from '../data';
import { BusyService } from '../services/busy.service';
import { ClientSessionService } from '../services/client-session.service';
import { ValidationResultsService } from '../services/validation-results.service';
import { CommandResponseTyped } from '../data/framework/commandResponse';
import * as moment from 'moment';
import * as _ from 'lodash';
import { NotificationService } from './notification.service';
import { HandledException, ValidationFeedbackUtility } from '../utilities';

@Injectable()
export class ExecuteCommandService {
  validationFeedbackUtility: ValidationFeedbackUtility = new ValidationFeedbackUtility();

  constructor(
    private busyService: BusyService,
    private clientSessionService: ClientSessionService,
    private validationResultsService: ValidationResultsService,
    private router: Router,
    private http: Http,
    private notificationService: NotificationService
  ) { }

  executeCommand(commandName: String, payload: any, useBusyIndicator: boolean = true, showNotificationOnSuccess: boolean = false): Promise<any> {
    this.validationResultsService.reset();
    this.notificationService.removePersistents();
    this.validationFeedbackUtility.removeInvalidClassFromElements();
    let commandRequest = this.preparePayload(commandName, payload);
    let promise = this.callExecuteCommand(commandRequest, showNotificationOnSuccess);
    if (useBusyIndicator) {
      this.incrementBusyCounter(promise);
    }
    this.prepareDecrementBusyCounter(promise, useBusyIndicator);
    console.log(promise);
    return promise;
  }

  private preparePayload(commandName: String, payload: any) {
    let now = new Date();
    let result = {
      CommandName: commandName,
      PayloadAsJson: JSON.stringify(payload),
      TimeZoneOffsetFromGMT: moment(now).utcOffset(),
      LogCommands: this.clientSessionService.userInfo.LogCommands
    };
    return result;
  }

  private incrementBusyCounter(promise: Promise<any>) {
    this.busyService.increment(promise);
  }

  private handleHttpResponse(response: any) {
    // HttpClient rejects non-2xx with HttpErrorResponse (no .json()). Parse that body first.
    if (this.isHttpErrorResponse(response)) {
      return this.parseHttpClientError(response);
    }

    // Already a parsed command response
    if (response && (response.Succeeded !== undefined || response.HasValidationResults !== undefined || response.ValidationResults)) {
      return response;
    }

    if (response && response.status === 401) {
      this.handleUnauthorized(response);
    }

    if (response != null && typeof response.json === 'function') {
      const contentType = response.headers && response.headers.get('content-type');
      if (contentType && contentType.toLowerCase().indexOf('application/json') >= 0) {
        return response.json();
      }
      // LegacyHttpResponse stores text; try parse even without content-type
      try {
        const parsed = response.json();
        if (parsed !== null && parsed !== undefined) {
          return parsed;
        }
      } catch {
        // fall through
      }
    }

    return this.createErrorResponse(response);
  }

  private isHttpErrorResponse(response: any): boolean {
    if (!response || typeof response.json === 'function') {
      return false;
    }
    return response.name === 'HttpErrorResponse'
      || response.error !== undefined
      || (typeof response.status === 'number' && response.status >= 400);
  }

  private parseHttpClientError(error: any): any {
    if (error && error.status === 401) {
      this.handleUnauthorized(error);
    }

    let body = error && error.error;
    if (typeof body === 'string') {
      try {
        body = body ? JSON.parse(body) : null;
      } catch {
        body = null;
      }
    }

    if (body && (body.Succeeded !== undefined || body.HasValidationResults || body.ValidationResults || body.Message)) {
      return body;
    }

    return this.createErrorResponse(error);
  }

  private handleUnauthorized(response: any): void {
    let json: any = null;
    try {
      if (typeof response.json === 'function') {
        json = response.json();
      } else if (response.error) {
        json = typeof response.error === 'string' ? JSON.parse(response.error) : response.error;
      }
    } catch {
      json = null;
    }

    const message = json && (json.Message || json.message);
    if (message == 'Not Authorized') {
      this.notificationService.danger('You are unauthorized to access this function.');
      return;
    }

    this.clientSessionService.reset();
    this.notificationService.danger('You are not logged in.', undefined, { timeOut: 10000, extendedTimeOut: 0, closeButton: true });
    const currentUrl = this.router.url || '/projects';
    if (currentUrl.indexOf('/login') !== 0) {
      this.router.navigate(['login'], { queryParams: { redirectRoute: currentUrl } });
    }
  }

  private callExecuteCommand(commandRequest: any, showNotificationOnSuccess: boolean): Promise<any> {
    let promise = this.http.post("/ExecuteCommand/" + commandRequest.CommandName, commandRequest)
      .toPromise()
      .then((response) => this.handleHttpResponse(response), (response) => this.handleHttpResponse(response))
      .then((response: CommandResponseTyped<object>) => this.onExecuteCommandCompleted(commandRequest.CommandName, response, showNotificationOnSuccess));
    return promise;
  }

  private createErrorResponse(response: any) {
    let msg = 'A call to the server failed. Please try again.';
    if (response) {
      if (response.status || response.statusText) {
        msg = `${response.status || ''} - ${response.statusText || 'Error'}`.trim();
      } else if (typeof response.message === 'string' && response.message) {
        msg = response.message;
      }
    }
    let errorResponse: Data.CommandResponse = {
      HasValidationResults: true,
      Message: msg,
      Payload: null,
      Succeeded: false,
      ValidationResults: [{
        ErrorMessage: msg,
        MemberNames: []
      }],
      Warnings: [],
    };
    return errorResponse;
  }

  private prepareDecrementBusyCounter(promise: Promise<any>, useBusyIndicator: boolean) {
    if (useBusyIndicator) {
      promise.then(
        () => this.busyService.decrement(),
        () => this.busyService.decrement()
      );
    }
  }

  protected onExecuteCommandCompleted(commandName: String, response: any, showNotificationOnSuccess: boolean, onCommandSucceeded?: (commandName: String, response: any) => void) {
    if (response && response.Warnings && response.Warnings.length > 0)
      this.notificationService.warningList(response.Warnings);
    if (response !== undefined && response !== null) {
      if (response.Succeeded === true) {
        this.onExecuteCommandSucceeded(showNotificationOnSuccess);
        if (onCommandSucceeded !== undefined) {
          onCommandSucceeded(commandName, response);
        }
      }
      else {
        this.onExecuteCommandFailed(response);
      }
    }
    else {
      this.onExecuteServiceCallFailed(commandName);
    }
    return response;
  }

  protected onExecuteCommandSucceeded(showNotificationOnSuccess: boolean) {
    if (showNotificationOnSuccess === true)
      this.notificationService.success('Succeeded!');
  }

  protected onExecuteCommandFailed(response: any) {
    if (response.HasValidationResults) {
      let messages: string[] = [];
      let memberNames: string[] = [];
      response.ValidationResults.forEach((o: Data.ValidationResult) => {
        messages.push(o.ErrorMessage);
        if (o.MemberNames !== undefined && o.MemberNames.length) {
          for (let i = 0; i < o.MemberNames.length; i++)
            if (o.MemberNames[i] !== undefined && o.MemberNames[i] !== '')
              memberNames.push(o.MemberNames[i]);
        }
      });
      this.validationFeedbackUtility.addInvalidClassToMatchingMemberNames(memberNames);
      this.notificationService.dangerList(messages);
    }
    else if (response.Message !== undefined && response.Message !== null) {
      this.notificationService.danger(response.Message);
    }
    let validationResultsMessages = _.map(response.ValidationResults, (value) => {
      return value.ErrorMessage;
    });
    throw new HandledException(validationResultsMessages);
  }

  protected onExecuteServiceCallFailed(commandName: String) {
    let message = 'A call to the server failed.  Please try again.';
    this.notificationService.danger(message);
    throw new HandledException(message += '  ---CommandName: ' + commandName);
  }

  getCxPlanner(projectId): Promise<any> {
    this.validationResultsService.reset();
    this.notificationService.removePersistents();
    this.validationFeedbackUtility.removeInvalidClassFromElements();

    return this.http.get(`/CallCxPlanner?projectId=`+projectId).toPromise()
      .then((response: any) => {
        return response;  
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        throw error;
      });
  }

}
